/**
 * Explore's Add Filter: pick a dimension, then values from a searchable list
 * of what this property actually has. A filter applies the moment a value is
 * ticked (no Apply button) and shows as a removable chip with Clear All.
 * "Channel Type" is different: it chooses which channel grouping the views
 * use, not a value to keep.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { reportsApi, type Filter, type RangeSetting } from '../../reports/reports-api.ts';
import {
  CHANNEL_GROUPINGS,
  DATE_INTERVALS,
  FILTER_DIMENSIONS,
  type DateInterval,
} from './ga4-views.ts';

const VALUE_LIMIT = 100;
/** Filters that choose a setting rather than keep values. */
const CHOICE_FILTERS = new Set(['channel_type', 'date_interval']);
const SEARCH_DELAY_MS = 300;

interface Props {
  organisationId: string;
  clientId: string;
  timezone: string;
  range: RangeSetting;
  dimensionLabel: (key: string) => string;
  filters: Filter[];
  onFilters: (filters: Filter[]) => void;
  grouping: string;
  onGrouping: (grouping: string) => void;
  interval: DateInterval;
  onInterval: (interval: DateInterval) => void;
}

function useValues(props: Props, dimension: string | null, search: string): string[] | null {
  const [values, setValues] = useState<string[] | null>(null);
  useEffect(() => {
    if (!dimension || CHOICE_FILTERS.has(dimension)) return undefined;
    let cancelled = false;
    setValues(null);
    const timer = window.setTimeout(() => {
      reportsApi
        .query(props.organisationId, {
          business_unit_id: props.clientId,
          mode: 'LIVE',
          timezone: props.timezone,
          range: props.range,
          widgets: [
            {
              id: 'values',
              source: 'ga4',
              metrics: ['sessions'],
              dimension,
              limit: VALUE_LIMIT,
              filters: search ? [{ dimension, op: 'contains', values: [search] }] : [],
            },
          ],
        })
        .then(
          ({ results }) =>
            !cancelled && setValues((results[0]?.rows ?? []).map((row) => row.label)),
        )
        .catch(() => !cancelled && setValues([]));
    }, SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [dimension, search, JSON.stringify(props.range)]);
  return values;
}

export function ExploreFilters(props: Props): ReactNode {
  const [open, setOpen] = useState(false);
  const [dimension, setDimension] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const values = useValues(props, dimension, search);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    const onPointer = (event: PointerEvent) =>
      box.current && !box.current.contains(event.target as Node) && setOpen(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  const current = props.filters.find((filter) => filter.dimension === dimension);
  const toggleValue = (value: string) => {
    if (!dimension) return;
    const chosen = current?.values ?? [];
    const next = chosen.includes(value)
      ? chosen.filter((item) => item !== value)
      : [...chosen, value];
    const others = props.filters.filter((filter) => filter.dimension !== dimension);
    props.onFilters(next.length > 0 ? [...others, { dimension, op: 'is', values: next }] : others);
  };

  return (
    <div className="ex-filter" ref={box}>
      <button
        type="button"
        className="btn secondary"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen(!open);
          setDimension(null);
          setSearch('');
        }}
      >
        Add Filter
      </button>
      {open && (
        <div className="ex-pop" role="dialog" aria-label="Add filter">
          {dimension === null && (
            <ul className="ex-pop-list">
              <li>
                <button type="button" onClick={() => setDimension('channel_type')}>
                  Channel Type
                </button>
              </li>
              {FILTER_DIMENSIONS.map((key) => (
                <li key={key}>
                  <button type="button" onClick={() => setDimension(key)}>
                    {props.dimensionLabel(key)}
                  </button>
                  {key === 'country' && (
                    <button type="button" onClick={() => setDimension('date_interval')}>
                      Date Interval
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {dimension === 'channel_type' && (
            <fieldset className="ex-pop-values">
              <legend>Channel Type</legend>
              {CHANNEL_GROUPINGS.map((key) => (
                <label key={key}>
                  <input
                    type="radio"
                    name="channel-grouping"
                    checked={props.grouping === key}
                    onChange={() => props.onGrouping(key)}
                  />
                  {props.dimensionLabel(key)}
                </label>
              ))}
            </fieldset>
          )}
          {dimension === 'date_interval' && (
            <fieldset className="ex-pop-values">
              <legend>Date Interval</legend>
              {DATE_INTERVALS.map((item) => (
                <label key={item.key}>
                  <input
                    type="radio"
                    name="date-interval"
                    checked={props.interval === item.key}
                    onChange={() => props.onInterval(item.key)}
                  />
                  {item.label}
                </label>
              ))}
            </fieldset>
          )}
          {dimension !== null && !CHOICE_FILTERS.has(dimension) && (
            <fieldset className="ex-pop-values">
              <legend>{props.dimensionLabel(dimension)}</legend>
              <input
                type="search"
                placeholder="Search"
                aria-label={`Search ${props.dimensionLabel(dimension)}`}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              {values === null && <p className="ex-none">Loading…</p>}
              {values?.length === 0 && <p className="ex-none">No values in this period.</p>}
              {values?.map((value) => (
                <label key={value}>
                  <input
                    type="checkbox"
                    checked={current?.values.includes(value) ?? false}
                    onChange={() => toggleValue(value)}
                  />
                  {value}
                </label>
              ))}
            </fieldset>
          )}
          {dimension !== null && (
            <button type="button" className="ex-link" onClick={() => setDimension(null)}>
              ← All filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}
